import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { RiDeleteBinLine, RiStarFill } from "@remixicon/react";
import { getTutorRatings } from "../../../services/dataService";
import { hasPermission, permissions } from "../../../config/permissions";
import { personName } from "../../../utils/studentDetail";
import { reviewsLine } from "../../../utils/tutorDetail";
import { allShowingLabel, dateTimeLines } from "../../../utils/kitPresentation";
import { tokens } from "../../../utils/figmaTokens";
import { TableCard } from "../../../components/kit/SectionCard";
import Pager from "../../../components/kit/Pager";
import { EyeButton, PersonCell, RowAction, TableMessageRow, TwoLineCell } from "../../../components/kit/TableParts";
import RemoveRatingDialog from "../../../components/RemoveRatingDialog/RemoveRatingDialog";
import { errorText } from "../../Students/tabs/TabState";
import { useUsersByIds } from "../../Students/tabs/useUsersByIds";

const COLUMNS = ["Date", "Student", "Rating", "Comment", "Actions"];

// Every rating the tutor received, newest first (047 D44; the drawer's list, 026 D24): who rated,
// the stars, the comment, the lesson. Remove is moderator and up, through RemoveRatingDialog;
// after it the page re-reads the tutor, so the head's count follows.
const RatingsTab = () => {
  const { tutor, actingUser, userRole, refreshTutor } = useOutletContext();
  const uid = tutor.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toRemove, setToRemove] = useState(null);
  const canModerate = hasPermission(userRole, permissions.MODERATE_RATINGS);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await getTutorRatings(uid));
    } catch (e) {
      console.error("Error loading ratings:", e);
      setError(errorText(e, "Failed to load the ratings."));
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const usersById = useUsersByIds(rows.map((r) => r.ratedBy));

  // The server's answer removes the row; null means it was already gone, so the list is re-read.
  const removed = (result) => {
    if (result) setRows((list) => list.filter((r) => r.id !== result.ratingId));
    else load();
    refreshTutor();
  };

  const settled = !loading && !error;

  return (
    <TableCard title="Ratings" subtitle={`${reviewsLine(tutor)} · newest first`} footer={<Pager label={settled ? allShowingLabel(rows.length) : ""} />}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading || error || rows.length === 0 ? (
              <TableMessageRow colSpan={COLUMNS.length} loading={loading} error={error} onRetry={load} emptyText="No ratings yet." />
            ) : (
              rows.map((r) => {
                const when = dateTimeLines(r.createdAt);
                const student = usersById[r.ratedBy];
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <TwoLineCell top={when ? when.date : "-"} bottom={when?.time} />
                    </TableCell>
                    <TableCell>{student ? <PersonCell name={personName(student)} colorKey={r.ratedBy} /> : <TwoLineCell top="-" />}</TableCell>
                    <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                        <RiStarFill size={13} color={tokens.star} aria-hidden />
                        {r.rating}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography variant="bodySmall" sx={{ whiteSpace: "pre-wrap", color: r.comment ? tokens.text.primary : tokens.text.tertiary }}>
                        {r.comment || "No comment"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {r.lessonId && <EyeButton to={`/lessons/${r.lessonId}`} label="Open lesson" />}
                      {canModerate && <RowAction Icon={RiDeleteBinLine} label="Remove rating" onClick={() => setToRemove(r)} />}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <RemoveRatingDialog
        open={!!toRemove}
        onClose={() => setToRemove(null)}
        tutor={tutor}
        rating={toRemove}
        actingUser={actingUser}
        onRemoved={removed}
      />
    </TableCard>
  );
};

export default RatingsTab;
